import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { TextField, IconButton } from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import styles from "./chatwindow.module.css";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { addUserMessage, sendQueryStream } from "../../store/chatSlice";
import CircularProgress from "@mui/material/CircularProgress";
import useEnhancePrompt from "../../hooks/useEnhancePrompt";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { ERROR_MESSAGES, MAX_MESSAGE_LENGTH } from "../../utils/constants";

export type MessageInputHandle = {
  // Programmatically send a prompt (auto-sends immediately)
  sendPrompt: (text: string) => Promise<void>;
};

type MessageInputProps = {};

const MessageInput = forwardRef<MessageInputHandle, MessageInputProps>(
  ({}: MessageInputProps, ref) => {
    const [value, setValue] = useState("");
    const { enhancing, enhance } = useEnhancePrompt();
    const dispatch = useDispatch<AppDispatch>();
    const { messages, isChatOpen, loading } = useSelector(
      (state: RootState) => state.chat
    );
    const inputRef = useRef<HTMLInputElement>(null);

    // Auto-focus input when chat opens or when starting a brand-new session
    useEffect(() => {
      if (isChatOpen || messages.length === 0) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }, [isChatOpen, messages.length]);

    const submit = useCallback(async () => {
      const trimmed = value.trim();

      // Validation
      if (!trimmed) {
        toast.warning(ERROR_MESSAGES.MESSAGE_EMPTY);
        return;
      }
      if (trimmed.length > MAX_MESSAGE_LENGTH) {
        toast.error(ERROR_MESSAGES.MESSAGE_TOO_LONG);
        return;
      }

      const userMessage = {
        id: uuidv4(),
        sender: "user" as const,
        text: trimmed,
      };

      // Optimistic append
      dispatch(addUserMessage(userMessage));

      // Clear input after capturing current value
      setValue("");

      // Send to backend
      await dispatch(sendQueryStream({ userMessage: trimmed }));
    }, [value, dispatch]);

    // Expose imperative API for parent components
    useImperativeHandle(
      ref,
      () => ({
        sendPrompt: async (text: string) => {
          setValue(text);
          setTimeout(() => {
            inputRef.current?.focus();
            // Auto-submit the prompt
            const trimmed = text.trim();
            if (!trimmed || trimmed.length > MAX_MESSAGE_LENGTH) return;

            const userMessage = {
              id: uuidv4(),
              sender: "user" as const,
              text: trimmed,
            };
            dispatch(addUserMessage(userMessage));
            setValue("");
            dispatch(sendQueryStream({ userMessage: trimmed }));
          }, 0);
        },
      }),
      [dispatch]
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
      // Shift+Enter -> newline (default behavior)
    };

    return (
      <div className={styles.messageInputContainer}>
        <div className={styles.inputBar}>
          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder="Please enter your question..."
            value={value}
            multiline
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            size="small"
            variant="outlined"
            disabled={loading}
          />
          {/* Disable  Enhance Prompt temporarily as endpoint is not ready*/}
          {/* <IconButton
            aria-label="Enhance Prompt"
            title={enhancing ? "Enhancing..." : "Enhance Prompt"}
            className={styles.enhanceButton}
            disabled={!value.trim() || enhancing}
            onClick={async () => {
              if (!value.trim() || enhancing) return;
              const enhanced = await enhance(value.trim());
              if (enhanced && enhanced.trim()) {
                setValue(enhanced);
              }
            }}
          >
            {enhancing ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
          </IconButton> */}
          <IconButton
            onClick={submit}
            title="Send"
            className={styles.sendButton}
            disabled={!value.trim() || loading}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <ArrowUpwardRoundedIcon />
            )}
          </IconButton>
        </div>
      </div>
    );
  }
);

export default MessageInput;
